import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import "react-toastify/dist/ReactToastify.css";
import {
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Progress,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import DropdownStatusNew from "components/Common/DropdownStatusNew";
import {
  COLOR_NOTICE_TYPE,
  COLOR_HASHTAG_STATUS,
  COLOR_USER_TYPE_LOG,
  LIST_NOTICE_TYPE,
  USER_TYPE_NOTICE_OPTION,
  STATUS_ACTIVE_DISABLE_OPTION,
} from "helpers/constans";
import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import { toast } from "react-toastify";
import { createNotice, updateNotice } from "store/notice/thunk";
import noticeApi from "api/noticeApi";
import ModalEditContent from "components/Common/ModalEditContent";
import { formatDateStringToKorea, formatISOString } from "helpers/format";
import "react-toastify/dist/ReactToastify.css";
import DatePicker, { registerLocale } from "react-datepicker";
import { get } from "lodash";
import EditContent from "components/Common/EditContent";

export interface Props {
  id?: string;
  item?: any;
  triggerRefresh?: () => void;
  setIsOpen?: any;
  setItemToEdit?: any;
}

registerLocale("en", en);
registerLocale("ko", ko);

const NoticeForm = ({
  id = "",
  item,
  triggerRefresh,
  setIsOpen,
  setItemToEdit,
}: Props) => {
  const { t, i18n } = useTranslation();

  const [isOpenEdit, setOpenEdit] = useState<any>();

  const onCloseEdit = () => {
    setOpenEdit(null);
  };

  const handleSubmitEdit = () => {
    setOpenEdit(null);
  };

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);

  const handleSubmit = async (values: any) => {
    try {
      setIsLoading((_prev) => true);
      const data: any = {
        title: values?.title,
        content: values?.content,
        type: values?.type,
        userType: values?.userType?.map((item: any) => item?.value) || "",
        isPinned: !!values?.isPinned,
        status:
          values?.status?.toString() === "0"
            ? "INACTIVE"
            : values?.status?.toString() === "1"
            ? "ACTIVE"
            : "",
      };

      if (values?.isShowTime) {
        data.displayStartAt = formatISOString(values?.displayStartAt);
        data.displayEndAt = formatISOString(values?.displayEndAt);
      }

      Object.entries(data).forEach((element) => {
        if (!element?.[1]) {
          delete data?.[element?.[0]];
        }
      });
      console.log("data", data);

      const response: any = id
        ? await updateNotice(id, data)
        : await createNotice(data);

      if (response?.data) {
        setIsLoading((_prev) => false);
        toast(
          `${id ? t("The notice has been updated.") : t("The notice has been created.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
        triggerRefresh && triggerRefresh();
      } else {
        setIsLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const validationSchema = Yup.object({
    title: Yup.string().required(`${t("This field is required")}`),
    content: Yup.string().required(`${t("This field is required")}`),
    type: Yup.string().required(`${t("This field is required")}`),
    userType: Yup.array()
      .min(1, `${t("This field is required")}`)
      .required(`${t("This field is required")}`),
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      content: "",
      type: "FAQ",
      userType: [{ label: "Job Seeker", value: "APPLICANT" }],
      displayStartAt: null,
      displayEndAt: null,
      status: "1",
      isPinned: false,
      isShowTime: false,
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const onCloseClick = () => {
    setIsOpen(false);
    setItemToEdit(false);
  };

  const handleSetValueForm = (value: any) => {
    const data: any = {
      title: value?.title,
      content: value?.content,
      type: value?.type,
      userType: value?.userType?.map((item: any) =>
        USER_TYPE_NOTICE_OPTION?.find((i: any) => i?.value === item)
      ),
      displayStartAt: value?.displayStartAt
        ? new Date(formatDateStringToKorea(value?.displayStartAt))
        : null,
      displayEndAt: value?.displayEndAt
        ? new Date(formatDateStringToKorea(value?.displayEndAt))
        : null,
      status: value?.status === "INACTIVE" ? "0" : "1",
      isPinned: !!value?.isPinned,
      isShowTime: !!value?.displayStartAt && !!value?.displayStartAt,
    };

    formik.setValues(data);
  };

  useEffect(() => {
    if (id) {
      const handleDetail = async () => {
        try {
          const response: any = await noticeApi.getDetails(item?.id);
          if (response?.data) {
            handleSetValueForm(response?.data);
          }
        } catch (error) {
          console.log(error);
        }
      };
      handleDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function getRequiredPathsFromSchema(schema: any, parentPath?: any): any {
    const requiredPaths = [];

    if (schema?.fields) {
      for (const key in schema.fields) {
        const field = schema.fields[key];
        const path = parentPath ? `${parentPath}.${key}` : key;

        if (field.type === "object") {
          requiredPaths.push(...getRequiredPathsFromSchema(field, path));
        } else if (field.type === "array") {
          const inner = field.innerType;
          if (inner?.type === "object") {
            requiredPaths.push(
              ...getRequiredPathsFromSchema(inner, `${path}[0]`)
            );
          } else {
            requiredPaths.push(path);
          }
        }

        const isRequired = field.tests?.some(
          (t: any) => t.OPTIONS?.name === "required"
        );
        if (isRequired) {
          requiredPaths.push(path);
        }
      }
    }

    return requiredPaths;
  }

  const calculateProgress = (values: any, errors: any) => {
    const requiredPaths = getRequiredPathsFromSchema(validationSchema);
    const filledCount = requiredPaths.filter((path: any) => {
      const val = get(values, path);
      const error = get(errors, path);
      return val !== "" && val !== undefined && !error;
    }).length;
    return Math.round((filledCount / requiredPaths.length) * 100);
  };

  useEffect(() => {
    document.title = `${t("Create Notice")} - ${t(
      "Notice Management"
    )} | OnnyJob`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  return (
    <React.Fragment>
      <Modal isOpen={true} centered={true} scrollable={false} size="lg">
        <ModalHeader toggle={isLoading ? () => {} : onCloseClick}>
          {id ? t("Update Notice") : t("Create Notice")}
        </ModalHeader>
        <ModalBody className="py-3 px-4">
          <div className="mb-4">
            <Progress
              value={calculateProgress(formik?.values, formik?.errors)}
              color="primary"
              className="animated-progress progress-sm"
            />
          </div>

          <div className="mt-3">
            <>
              {isLoadingDetail && (
                <div
                  style={{
                    position: "absolute",
                    zIndex: 3,
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    backgroundColor: "rgb(164 164 164 / 36%)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Spinner size="sm" color="primary"></Spinner>
                </div>
              )}
              <form onSubmit={formik.handleSubmit}>
                <Row>
                  <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="title" className="form-limitType">
                        {t("Title")} <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id="title"
                        name="title"
                        type="text"
                        className="form-control"
                        placeholder={t("Title")}
                        value={formik?.values?.title}
                        onChange={(event: any) =>
                          formik.setFieldValue("title", event?.target?.value)
                        }
                      />
                      {formik.touched.title && formik.errors.title ? (
                        <div className="text-danger mt-2">
                          {formik.errors.title}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={12}>
                    <div className="mb-3">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <Label htmlFor="content" className="form-label">
                          {t("Content")} <span className="text-danger">*</span>
                        </Label>
                        <div
                          className="form-control d-flex align-items-center justify-content-center cursor-pointer"
                          style={{
                            minWidth: "30px",
                            width: "30px",
                            height: "30px",
                            padding: 0,
                          }}
                          onClick={() => {
                            setOpenEdit({
                              title: t("Content"),
                              width: "1200px",
                              height: "812px",
                              value: formik?.values?.content || "",
                              isView: true,
                              cb: (event: any) => {
                                formik.setFieldValue(`content`, event);
                              },
                            });
                          }}
                        >
                          <i className="ri-eye-fill" />
                        </div>
                      </div>
                      <div className="editContent" style={{ height: "300px" }}>
                        <EditContent
                          value={formik?.values?.content}
                          onChange={(event: any) => {
                            formik.setFieldValue(`content`, event);
                          }}
                        />
                      </div>
                      {formik.touched.content && formik.errors.content ? (
                        <div className="text-danger mt-2">
                          {formik.errors.content}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label className="form-label">{t("Type")}</Label>

                      <DropdownStatusNew
                        name="type"
                        dataList={LIST_NOTICE_TYPE || []}
                        placeholder={`${t("Select type")}...`}
                        className="dropdown-status-rounded"
                        classNamePrefix="name-prefix"
                        initialValue={formik.values.type}
                        onChangeSelect={(e: any) => {
                          formik.setFieldValue("type", e?.value);
                        }}
                        colors={COLOR_NOTICE_TYPE}
                      />

                      {formik.touched.type && formik.errors.type ? (
                        <div className="text-danger mt-2">
                          {formik.errors.type}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label className="form-label">
                        {t("User Type")} <span className="text-danger">*</span>
                      </Label>

                      <DropdownStatusNew
                        name="userType"
                        dataList={USER_TYPE_NOTICE_OPTION || []}
                        placeholder={`${t("Select type")}...`}
                        className="dropdown-status-rounded"
                        classNamePrefix="name-prefix"
                        initialValue={formik.values.userType}
                        onChangeSelect={(e: any) => {
                          formik.setFieldValue("userType", e);
                        }}
                        isMulti
                        colors={COLOR_USER_TYPE_LOG}
                      />

                      {formik.touched.userType && formik.errors.userType ? (
                        <div className="text-danger mt-2">
                          {formik.errors.userType + ""}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label className="form-label">{t("Status")}</Label>

                      <DropdownStatusNew
                        name="status"
                        dataList={STATUS_ACTIVE_DISABLE_OPTION || []}
                        placeholder={`${t("Select status")}...`}
                        className="dropdown-status-rounded"
                        classNamePrefix="name-prefix"
                        initialValue={+formik.values.status}
                        onChangeSelect={(e: any) => {
                          formik.setFieldValue("status", e?.value?.toString());
                        }}
                        colors={COLOR_HASHTAG_STATUS}
                      />
                    </div>
                  </Col>

                  <Col lg={6} />

                  <Col lg={6} className="d-flex align-items-end">
                    <div className="mb-3 d-flex align-items-center">
                      <Input
                        className="form-check-input mt-0"
                        type="checkbox"
                        id="isShowTime"
                        name="isShowTime"
                        checked={!!formik.values?.isShowTime}
                        onChange={(event: any) => {
                          formik.setFieldValue(
                            "isShowTime",
                            event?.target?.checked
                          );
                        }}
                        style={{ cursor: "default" }}
                      />
                      <Label
                        htmlFor="isShowTime"
                        className="form-label mx-2 my-0"
                      >
                        {t("Show time display")}
                      </Label>
                    </div>
                  </Col>
                  <Col lg={6} className="d-flex align-items-end">
                    <div className="mb-3 d-flex align-items-center">
                      <Input
                        className="form-check-input mt-0"
                        type="checkbox"
                        id="isPinned"
                        name="isPinned"
                        checked={!!formik.values?.isPinned}
                        onChange={(event: any) => {
                          formik.setFieldValue(
                            "isPinned",
                            event?.target?.checked
                          );
                        }}
                        style={{ cursor: "default" }}
                      />
                      <Label
                        htmlFor="isPinned"
                        className="form-label mx-2 my-0"
                      >
                        {t("IsPined")}
                      </Label>
                    </div>
                  </Col>

                  {!!formik.values?.isShowTime && (
                    <React.Fragment>
                      <Col lg={6} className="date-picker-wrapper-custom">
                        <Label className="form-label">
                          {t("Display Start At")}
                        </Label>
                        <div className="w-100">
                          <DatePicker
                            name={"displayStartAt"}
                            className="form-control"
                            selected={formik?.values?.displayStartAt}
                            placeholderText={`${t("Select Date")}`}
                            onChange={(date: any) => {
                              formik.setFieldValue(
                                "displayStartAt",
                                date || ""
                              );
                            }}
                            locale={"ko"}
                            popperPlacement="auto"
                            wrapperClassName="w-100"
                            dateFormat="yyyy-MM-dd HH:mm:ss"
                            showTimeSelect
                          />
                        </div>
                      </Col>
                      <Col lg={6} className="date-picker-wrapper-custom">
                        <Label className="form-label">
                          {t("Display End At")}
                        </Label>
                        <div className="w-100">
                          <DatePicker
                            name={"displayEndAt"}
                            className="form-control"
                            selected={formik?.values?.displayEndAt}
                            placeholderText={`${t("Select Date")}`}
                            onChange={(date: any) => {
                              formik.setFieldValue("displayEndAt", date || "");
                            }}
                            locale={"ko"}
                            popperPlacement="auto"
                            wrapperClassName="w-100"
                            dateFormat="yyyy-MM-dd HH:mm:ss"
                            showTimeSelect
                          />
                        </div>
                      </Col>
                    </React.Fragment>
                  )}

                  <Col lg={12}>
                    <div className="hstack gap-2 justify-content-end mt-3">
                      <button type="submit" className="btn btn-primary fs-14">
                        {isLoading ? (
                          <Spinner size="sm" className="me-2"></Spinner>
                        ) : !!id ? (
                          <i className="ri-login-circle-line align-bottom me-2 "></i>
                        ) : (
                          <i className="ri-add-fill align-bottom me-2"></i>
                        )}
                        {id ? t("Update Notice") : t("Create Notice")}
                      </button>
                    </div>
                  </Col>
                </Row>
              </form>
            </>
          </div>
        </ModalBody>
      </Modal>
      <ModalEditContent
        header={isOpenEdit?.title || t("Content")}
        isOpen={isOpenEdit}
        isView={isOpenEdit?.isView}
        onClose={onCloseEdit}
        onConfirm={handleSubmitEdit}
      />
    </React.Fragment>
  );
};

export default NoticeForm;
